import React, {useState} from 'react';
import {Button, message, Modal} from 'antd';
import {CopyOutlined, FileExcelOutlined} from '@ant-design/icons';
import {useNavigate, useLocation} from 'react-router-dom';
import {t} from '../../../../i18n';
import {DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY} from '../../../../constants/deliveryDownloadNavigation';

interface ParcelsDownloadModalProps {
    visible: boolean;
    onClose: () => void;
    fileName: string;
}

const ParcelsDownloadModal: React.FC<ParcelsDownloadModalProps> = ({
                                                                       visible,
                                                                       onClose,
                                                                       fileName,
                                                                   }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const [copyLoading, setCopyLoading] = useState(false);

    const handleCopyFileName = async () => {
        setCopyLoading(true);
        try {
            await navigator.clipboard.writeText(fileName);
            message.success(t('delivery.parcels.message.downloadCopySuccess'));
        } catch (error) {
            message.error(t('delivery.parcels.message.downloadCopyFailed'));
        } finally {
            setCopyLoading(false);
        }
    };

    const handleGoToDownloadPage = () => {
        // 按当前页面所属端跳转到对应下载页，命中前缀则去该端下载页，否则回退到总部下载页
        const downloadPageByModule: Array<[prefix: string, path: string]> = [
            ['/qualitycontrol', '/qualitycontrol/download'],
            ['/wms', '/wms/download'],
            // 罚单管理中心在 /partner-control 与 /delivery 下各挂载一次，两处都落到配送端下载页
            ['/partner-control', '/delivery/download'],
            ['/delivery', '/delivery/download'],
        ];
        const matched = downloadPageByModule.find(([prefix]) => location.pathname.includes(prefix));
        const targetPath = matched ? matched[1] : '/central/download';

        // 从罚单管理中心进入时，携带 state 使下载页预设「罚单管理中心导出」过滤类型
        const isPenaltyCenter =
            location.pathname.includes('penalty-center') || location.pathname.includes('penaltyCenter');
        const navigateOptions = isPenaltyCenter
            ? { state: { [DELIVERY_DOWNLOAD_FROM_PARTNER_PENALTY_STATE_KEY]: true } }
            : undefined;

        navigate(targetPath, navigateOptions);
        onClose();
    };

    return (
        <Modal
            title={
                <div className="flex items-center">
                    <div className="w-1 h-6 bg-purple-500 mr-3"></div>
                    <span className="text-lg font-medium">{t('delivery.parcels.title.downloadReport')}</span>
                </div>
            }
            open={visible}
            onCancel={onClose}
            width={600}
            footer={[
                <Button key="close" onClick={onClose}>
                    {t('components.button.close')}
                </Button>,
                <Button key="copy" icon={<CopyOutlined/>} loading={copyLoading} onClick={handleCopyFileName}>
                    {t('components.button.copyFilename')}
                </Button>,
                <Button key="download" type="primary" onClick={handleGoToDownloadPage}>
                    {t('components.button.goToDownloadPage')}
                </Button>
            ]}
        >
            <div className="py-4">
                <p className="text-gray-800 font-medium mb-4">
                    {t('delivery.parcels.message.downloadDescription')}
                </p>
                <div className="bg-gray-50 p-3 rounded-lg mb-4">
                    <span className="font-medium">{t('delivery.parcels.label.filename')}: </span>
                    <code className="break-all">{fileName}</code>
                </div>
            </div>
        </Modal>
    );
};

export default ParcelsDownloadModal;